import React, {ComponentProps, ReactElement, useMemo, useState} from 'react';

import {useProductionCalculator} from '../../decision/productionCalculator';
import {SIMPLE_CALCULATOR_CROPS_NAMES, SimpleCalculatorCrop} from '../../decision/types';
import Select from '../Select';
import SimpleCalculatorCard from './SimpleCalculatorCard';
import SimpleCalculatorTable from './SimpleCalculatorTable';
import ValueWithUnit from './ValueWithUnit';

type Props = {};

const areaOptions = [1, 3, 5, 10, 20, 50].map(area => ({
  label: `${area} ha`,
  value: area,
}));

const cropOptions = Object.entries(SIMPLE_CALCULATOR_CROPS_NAMES).map(([cropId, name]) => ({
  label: name,
  value: cropId,
}));

const LargeScaleCalculator = ({}: Props): ReactElement => {
  const [availableLand, setAvailableLand] = useState<number>();
  const [cropId, setCropId] = useState<SimpleCalculatorCrop>();

  const input = {availableLand, cropId: cropId as SimpleCalculatorCrop | undefined};
  const areaName = availableLand !== undefined ? `${availableLand} ha` : 'Select';
  const cropName = cropId ? SIMPLE_CALCULATOR_CROPS_NAMES[cropId] : 'Select';

  const {data} = useProductionCalculator(input);

  const values = useMemo<ComponentProps<typeof SimpleCalculatorTable>['values']>(
    () => [
      [
        {content: 'Land area available', borderL: 'both'},
        {
          type: 'input',
          content: (
            <Select
              floating
              variant={'table'}
              buttonContent={areaName}
              options={areaOptions}
              selectedValues={availableLand !== undefined ? [availableLand] : []}
              onOptionSelected={value => setAvailableLand(value)}
              buttonClassName={'rounded-r-2xl'}
            />
          ),
          borderR: 'both',
        },
      ],
      [
        {content: 'Crop choice', borderL: 'top'},
        {
          type: 'input',
          content: (
            <Select
              floating
              variant={'table'}
              buttonContent={cropName}
              options={cropOptions}
              selectedValues={cropId ? [cropId] : []}
              onOptionSelected={value => setCropId(value as SimpleCalculatorCrop)}
              buttonClassName={'rounded-tr-2xl'}
            />
          ),
          borderR: 'top',
        },
      ],
      [
        {content: 'Moisture content of feedstock when harvested', borderL: 'middle'},
        {content: <ValueWithUnit value={data?.harvestedMC} unit={'%'} />, borderR: 'middle'},
      ],
      [
        {content: 'Typical generic yield of feedstock at required MC', borderL: 'bottom'},
        {content: <ValueWithUnit value={data?.genericYield} unit={'tonnes/ha'} />, borderR: 'bottom'},
      ],
      [
        {content: 'Potential tonnage harvested from mature crop'},
        {content: <ValueWithUnit value={data?.matureProduction} unit={'tonnes'} />},
      ],
    ],
    [areaName, availableLand, cropId, cropName, data?.genericYield, data?.harvestedMC, data?.matureProduction]
  );

  return (
    <SimpleCalculatorCard title={'How much can I produce?'}>
      <SimpleCalculatorTable values={values} />
    </SimpleCalculatorCard>
  );
};

export default LargeScaleCalculator;
